import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { UserService } from '@/core/services/user.service';
import { ProfileSwitcherComponent } from '@/layouts/components/profile-switcher.component';
import { Router } from '@angular/router';
import { Module, Permission } from '@shared/models';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@/core/services/auth.service';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'cis-user-dropdown',
  imports: [AvatarModule, PopoverModule, ProfileSwitcherComponent, ButtonModule, TranslatePipe],
  template: `
    <p-button text size="small" styleClass="justify-content-start" (click)="op.toggle($event)">
      <p-avatar class="font-medium" [label]="fallback()" size="large" shape="circle" />
      <div class="gap-4 flex flex-column justify-content-center align-items-start">
        <p class="text-lg -mb-4 font-medium">{{ name() }}</p>
        <cis-profile-switcher />
      </div>
    </p-button>
    <p-popover #op>
      <ng-template #content>
        <div id="user-dropdown-content" class="w-full h-full flex flex-column gap-2">
          @if (isExternal()) {
            <p-button
              text
              size="small"
              [label]="'common.change_password' | translate"
              icon="pi pi-lock-open"
              (click)="changePassword($event)" />
          }
          <p-button text size="small" [label]="'common.log_out' | translate" icon="pi pi-sign-out" (click)="logoutConfirm($event)" />
        </div>
      </ng-template>
    </p-popover>
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDropdownComponent {
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  readonly translateService = inject(TranslateService);

  currentUser = this.userService.currentUser;
  isExternal = computed(() => this.currentUser()?.isExternal || false);
  fullName = computed(() => this.currentUser()?.fullName || '');
  name = computed(() => this.fullName().split(' ').reverse().join(' '));
  email = computed(() => this.currentUser()?.email || '');
  fallback = computed(() => {
    return this.name()
      .split(' ')
      .map((t) => t[0])
      .join('');
  });

  hasGuidelinesPermissions = toSignal(this.userService.hasPermissionAsync(Module.Guideline, Permission.Read));

  logoutConfirm(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.translateService.instant('common.dialog.log_out.header'),
      acceptButtonProps: {
        severity: 'danger',
        label: this.translateService.instant('common.log_out'),
        icon: 'none',
      },
      rejectButtonProps: {
        severity: 'contrast',
        label: this.translateService.instant('common.cancel'),
        icon: 'none',
        text: true,
      },
      accept: () => {
        this.authService.logout({
          onRedirectNavigate: () => {
            this.router.navigate(['/account/login']);
          },
        });
      },
    });
  }
  changePassword(event: Event) {
    this.authService.changePassword(this.email());

    event.preventDefault();
    window.location.pathname = '';
  }
}
